import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import FilterIcon from '../icons/FilterIcon';
import UIButton from 'components/buttons/UIButton';
import styles from './RecommendationPage.module.scss';
import FilterField from './FilterField';
import PlanYearToggle from './PlanYearToggle';
import XCloseIcon from 'components/icons/XCloseIcon';
import { formatGapFilterLabel } from 'utils/formatters';
import { wrapFocus } from 'utils/focus';

const hasSelectionChanged = (pending: string[], active: string[]): boolean =>
  pending.length !== active.length || pending.some((v) => !active.includes(v));

const PANEL_TITLE_ID = 'filter-panel-title';
const FILTER_TRIGGER_ID = 'mobile-filter-trigger';
const FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type FilterMobileProps = {
  activeType: string;
  activeNetworks: string[];
  networkOptions: string[];
  activeCarriers: string[];
  carrierOptions: string[];
  activeLetters: string[];
  letterOptions: string[];
  years: string[];
  selectedYear: string;
  onYearChange: (year: string) => void;
  onApply: (networks: string[], carriers: string[], letters: string[]) => void;
};

const FilterMobile = ({
  activeType,
  activeNetworks,
  networkOptions,
  activeCarriers,
  carrierOptions,
  activeLetters,
  letterOptions,
  years,
  selectedYear,
  onYearChange,
  onApply,
}: FilterMobileProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [networkTypes, setNetworkTypes] = useState(activeNetworks);
  const [carriers, setCarriers] = useState(activeCarriers);
  const [letters, setLetters] = useState(activeLetters);

  const panelRef = useRef<HTMLDivElement>(null);
  const hasOpenedRef = useRef(false);

  // Manage inert (keeps closed panel out of tab order) and move focus on open/close
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (isOpen) {
      panel.removeAttribute('inert');
      const firstFocusable =
        panel.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
      firstFocusable?.focus();
    } else {
      panel.setAttribute('inert', '');
      if (hasOpenedRef.current) {
        document.getElementById(FILTER_TRIGGER_ID)?.focus();
      }
    }
  }, [isOpen]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        closePanel();
        return;
      }
      if (e.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => el.offsetParent !== null);

      wrapFocus(e, focusable);
    },
    [closePanel]
  );

  const syncFromProps = () => {
    setNetworkTypes(activeNetworks);
    setCarriers(activeCarriers);
    setLetters(activeLetters);
  };

  const openPanel = () => {
    syncFromProps();
    hasOpenedRef.current = true;
    setIsOpen(true);
  };

  const handleApply = () => {
    onApply(networkTypes, carriers, letters);
    closePanel();
  };

  const makeFilterToggle =
    (values: string[], setter: (v: string[]) => void) =>
    (e: ChangeEvent<HTMLFormElement>) => {
      const { value } = e.target as unknown as HTMLInputElement;
      setter(
        values.includes(value)
          ? values.filter((v) => v !== value)
          : [...values, value]
      );
    };

  const handleNetworkChange = makeFilterToggle(networkTypes, setNetworkTypes);
  const handleCarrierChange = makeFilterToggle(carriers, setCarriers);
  const handleLetterChange = makeFilterToggle(letters, setLetters);

  const hasChanges =
    hasSelectionChanged(networkTypes, activeNetworks) ||
    hasSelectionChanged(carriers, activeCarriers) ||
    hasSelectionChanged(letters, activeLetters);

  const disableApply = !hasChanges;

  return (
    <>
      {/* Mobile filter button */}
      <div className={styles.filterButton}>
        <UIButton
          buttonType="secondary"
          id={FILTER_TRIGGER_ID}
          label="Filter"
          icon={<FilterIcon />}
          iconPosition="left"
          onClick={openPanel}
          aria-expanded={isOpen}
          aria-controls="filter-panel"
          fullWidth={true}
        />
      </div>

      {/* Flyout menu */}
      <div
        id="filter-panel"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={PANEL_TITLE_ID}
        className={`${styles.filterPanel} ${
          isOpen ? styles.filterPanelOpen : ''
        }`}
        onKeyDown={handlePanelKeyDown}
      >
        <div className={styles.filterPanelHeader}>
          <span id={PANEL_TITLE_ID} className={styles.filterPanelTitle}>
            Filter Plans
          </span>
          <UIButton
            buttonType="tonal"
            label=""
            aria-label="Close filter panel"
            className={styles.filterPanelClose}
            onClick={closePanel}
            icon={<XCloseIcon />}
          />
        </div>

        <div className={styles.filterPanelContent}>
          <PlanYearToggle
            years={years}
            selectedYear={selectedYear}
            onChange={onYearChange}
          />
          {activeType === 'mapd' && (
            <FilterField
              title="Network type"
              name="network"
              options={networkOptions}
              active={networkTypes}
              onChange={handleNetworkChange}
            />
          )}
          {activeType === 'gap' && (
            <FilterField
              title="Plan letter"
              name="letter"
              options={letterOptions}
              active={letters}
              onChange={handleLetterChange}
              formatLabel={formatGapFilterLabel}
            />
          )}
          <FilterField
            title="Carriers"
            name="carrier"
            options={carrierOptions}
            active={carriers}
            onChange={handleCarrierChange}
          />
        </div>

        <div className={styles.filterPanelActions}>
          <UIButton
            buttonType="primary"
            label="Update filters"
            className={styles.filterPanelApply}
            onClick={handleApply}
            disabled={disableApply}
          />
        </div>
      </div>

      {isOpen && (
        <div
          className={styles.filterPanelOverlay}
          aria-hidden="true"
          onClick={closePanel}
        />
      )}
    </>
  );
};

export default FilterMobile;
